import { SistemaEnum, SistemaId } from '../../enum/sistema/sistema.enum';

/**
 * ✅ SINGLE SOURCE OF TRUTH para permissões do monorepo
 *
 * Padrão: {Sistema}.{Module}.{Action}
 *
 * IMPORTANTE: Quando alterar no backend (@ClaimModule ou @Permission),
 * altere AQUI também para manter sincronizado!
 *
 * Gerado a partir dos controllers com decoradores @ClaimModule e @Permission
 */
export const PERMISSIONS = {
  // ========== NOMOH - PESSOAS (/pessoa/adminController) ==========
  /**
   * Listar pessoas com filtros e paginação
   * Controller: /pessoa/adminController
   * Método: findAll
   */
  NOMOH_PESSOAS_LIST: 'Nomoh.pessoas.list',

  /**
   * Visualizar detalhes de uma pessoa específica
   * Controller: /pessoa/adminController
   * Métodos: findById, findPlanos, infoPessoas
   */
  NOMOH_PESSOAS_READ: 'Nomoh.pessoas.read',

  /**
   * Atualizar dados de pessoa ou associar/desvincular planos
   * Controller: /pessoa/adminController
   * Métodos: update, associarPlano, desvincularPlano
   */
  NOMOH_PESSOAS_UPDATE: 'Nomoh.pessoas.update',

  /**
   * Excluir pessoa do sistema
   * Controller: /pessoa/adminController
   * Método: delete
   */
  NOMOH_PESSOAS_DELETE: 'Nomoh.pessoas.delete',

  // ========== NOMOH - PERMISSIONS (PermissionController) ==========
  /**
   * Listar permissões com filtros e paginação
   * Controller: PermissionController
   * Método: list
   */
  NOMOH_PERMISSIONS_LIST: 'Nomoh.permissions.list',

  /**
   * Criar nova permissão manualmente
   * Controller: PermissionController
   * Método: create
   */
  NOMOH_PERMISSIONS_CREATE: 'Nomoh.permissions.create',

  /**
   * Atualizar permissão existente
   * Controller: PermissionController
   * Método: update
   */
  NOMOH_PERMISSIONS_UPDATE: 'Nomoh.permissions.update',

  /**
   * Remover ou desativar permissão (soft delete)
   * Controller: PermissionController
   * Método: delete
   */
  NOMOH_PERMISSIONS_DELETE: 'Nomoh.permissions.delete',

  // ========== NOMOH - PLANOS (PlanoController) ==========
  /**
   * Listar planos com filtros e paginação
   * Controller: PlanoController
   * Método: findAll
   */
  NOMOH_PLANOS_LIST: 'Nomoh.planos.list',

  /**
   * Criar novo plano
   * Controller: PlanoController
   * Método: create
   */
  NOMOH_PLANOS_CREATE: 'Nomoh.planos.create',

  /**
   * Visualizar detalhes de um plano específico
   * Controller: PlanoController
   * Método: findById
   */
  NOMOH_PLANOS_READ: 'Nomoh.planos.read',

  /**
   * Atualizar plano existente
   * Controller: PlanoController
   * Método: update
   */
  NOMOH_PLANOS_UPDATE: 'Nomoh.planos.update',

  /**
   * Excluir plano do sistema
   * Controller: PlanoController
   * Método: delete
   */
  NOMOH_PLANOS_DELETE: 'Nomoh.planos.delete',

  // ========== NOMOH - SISTEMAS (SistemaController) ==========
  /**
   * Listar sistemas com filtros e paginação
   * Controller: SistemaController
   * Método: findAll
   */
  NOMOH_SISTEMAS_LIST: 'Nomoh.sistemas.list',

  /**
   * Criar novo sistema
   * Controller: SistemaController
   * Método: create
   */
  NOMOH_SISTEMAS_CREATE: 'Nomoh.sistemas.create',

  /**
   * Visualizar detalhes de um sistema específico
   * Controller: SistemaController
   * Método: findById
   */
  NOMOH_SISTEMAS_READ: 'Nomoh.sistemas.read',

  /**
   * Atualizar sistema existente
   * Controller: SistemaController
   * Método: update
   */
  NOMOH_SISTEMAS_UPDATE: 'Nomoh.sistemas.update',

  /**
   * Excluir sistema do portal
   * Controller: SistemaController
   * Método: delete
   */
  NOMOH_SISTEMAS_DELETE: 'Nomoh.sistemas.delete',

  // ========== NOMOH - DESCONTOS (DescontoController) ==========
  /**
   * Criar novo desconto/cupom
   * Controller: DescontoController
   * Método: create
   * @Permission({ action: 'create' })
   */
  NOMOH_DESCONTOS_CREATE: 'Nomoh.descontos.create',

  /**
   * Validar token de cupom
   * Controller: DescontoController
   * Método: validarToken
   * @Permission({ action: 'validate' })
   */
  NOMOH_DESCONTOS_VALIDATE: 'Nomoh.descontos.validate',

  /**
   * Listar descontos e visualizar detalhes
   * Controller: DescontoController
   * Métodos: findAll, findById
   * @Permission({ action: 'read' })
   */
  NOMOH_DESCONTOS_READ: 'Nomoh.descontos.read',

  /**
   * Atualizar desconto existente
   * Controller: DescontoController
   * Método: update
   * @Permission({ action: 'update' })
   */
  NOMOH_DESCONTOS_UPDATE: 'Nomoh.descontos.update',

  /**
   * Excluir desconto do sistema
   * Controller: DescontoController
   * Método: delete
   * @Permission({ action: 'delete' })
   */
  NOMOH_DESCONTOS_DELETE: 'Nomoh.descontos.delete',

  // ========== NOMOH - HISTÓRICO DE PLANOS (PlanoHistoricoController) ==========
  /**
   * Vincular plano a pessoa com histórico
   * Controller: PlanoHistoricoController
   * Método: vincular
   * @Permission({ action: 'vincular' })
   */
  NOMOH_PLANO_HISTORICO_VINCULAR: 'Nomoh.plano-historico.vincular',

  /**
   * Buscar histórico e gerar relatórios
   * Controller: PlanoHistoricoController
   * Métodos: findByPessoa, relatorio
   * @Permission({ action: 'read' })
   */
  NOMOH_PLANO_HISTORICO_READ: 'Nomoh.plano-historico.read',

  /**
   * Cancelar plano manualmente
   * Controller: PlanoHistoricoController
   * Método: cancelar
   * @Permission({ action: 'cancelar' })
   */
  NOMOH_PLANO_HISTORICO_CANCELAR: 'Nomoh.plano-historico.cancelar',

  // ========== NOMOH - PAGAMENTOS (PagamentoController) ==========
  /**
   * Criar checkout de pagamento
   * Controller: PagamentoController
   * Método: checkout
   * @Permission({ action: 'create' })
   */
  NOMOH_PAGAMENTOS_CREATE: 'Nomoh.pagamentos.create',

  /**
   * Listar pagamentos e buscar detalhes
   * Controller: PagamentoController
   * Métodos: findAll, findById, findByPessoa
   * @Permission({ action: 'read' })
   */
  NOMOH_PAGAMENTOS_READ: 'Nomoh.pagamentos.read',

  /**
   * Cancelar e reativar assinaturas
   * Controller: PagamentoController
   * Métodos: cancelar, reativar
   * @Permission({ action: 'update' })
   */
  NOMOH_PAGAMENTOS_UPDATE: 'Nomoh.pagamentos.update',

  /**
   * Criar reembolsos
   * Controller: PagamentoController
   * Método: reembolso
   * @Permission({ action: 'delete' })
   */
  NOMOH_PAGAMENTOS_DELETE: 'Nomoh.pagamentos.delete',

  /**
   * Gerar relatórios de faturamento
   * Controller: PagamentoController
   * Método: relatorio/faturamento
   * @Permission({ action: 'report' })
   */
  NOMOH_PAGAMENTOS_REPORT: 'Nomoh.pagamentos.report',

  /**
   * Operações administrativas (vincular, sincronizar)
   * Controller: PagamentoController
   * Métodos: admin/vincular, admin/sincronizar-stripe
   * @Permission({ action: 'admin' })
   */
  NOMOH_PAGAMENTOS_ADMIN: 'Nomoh.pagamentos.admin',

  // ========== NOMOH - TEST (AppController) ==========
  /**
   * Visualizar dados de teste (público via @AllowAnonymous)
   * Controller: AppController
   * Método: getData
   */
  NOMOH_TEST_VIEW: 'Nomoh.test.view',

  NOMOH_TO_DO: 'Nomoh.to.do',

  // ========== BARDO - AGENDAS (AgendaController) ==========
  BARDO_AGENDAS_LIST: 'Bardo.agendas.list',
  BARDO_AGENDAS_READ: 'Bardo.agendas.read',
  BARDO_AGENDAS_CREATE: 'Bardo.agendas.create',
  BARDO_AGENDAS_UPDATE: 'Bardo.agendas.update',
  BARDO_AGENDAS_DELETE: 'Bardo.agendas.delete',
  /**
   * Criar encaixe manual na agenda (agendamento extraordinário, ignora
   * validação de disponibilidade/conflito de horário).
   * Controller: AgendaController / Rota: PUT inserirEncaixe
   */
  BARDO_AGENDAS_CRIAR_ENCAIXE: 'Bardo.agendas.criarEncaixe',

  // ========== BARDO - CLIENTES (ClienteController) ==========
  BARDO_CLIENTE_LIST: 'Bardo.cliente.list',
  BARDO_CLIENTE_READ: 'Bardo.cliente.read',
  BARDO_CLIENTE_CREATE: 'Bardo.cliente.create',
  BARDO_CLIENTE_UPDATE: 'Bardo.cliente.update',
  BARDO_CLIENTE_DELETE: 'Bardo.cliente.delete',

  // ========== BARDO - ESTABELECIMENTOS (EstabelecimentoController) ==========
  BARDO_ESTABELECIMENTOS_LIST: 'Bardo.estabelecimentos.list',
  BARDO_ESTABELECIMENTOS_READ: 'Bardo.estabelecimentos.read',
  BARDO_ESTABELECIMENTOS_CREATE: 'Bardo.estabelecimentos.create',
  BARDO_ESTABELECIMENTOS_UPDATE: 'Bardo.estabelecimentos.update',
  BARDO_ESTABELECIMENTOS_DELETE: 'Bardo.estabelecimentos.delete',

  // ========== BARDO - PROFISSIONAIS (ProfissionalController) ==========
  BARDO_PROFISSIONAIS_LIST: 'Bardo.profissionais.list',
  BARDO_PROFISSIONAIS_READ: 'Bardo.profissionais.read',
  BARDO_PROFISSIONAIS_CREATE: 'Bardo.profissionais.create',
  BARDO_PROFISSIONAIS_UPDATE: 'Bardo.profissionais.update',
  BARDO_PROFISSIONAIS_DELETE: 'Bardo.profissionais.delete',

  // ========== BARDO - TIPOS DE SERVIÇO (TipoServicoController) ==========
  BARDO_TIPOS_SERVICO_LIST: 'Bardo.tipos-servico.list',
  BARDO_TIPOS_SERVICO_READ: 'Bardo.tipos-servico.read',
  BARDO_TIPOS_SERVICO_CREATE: 'Bardo.tipos-servico.create',
  BARDO_TIPOS_SERVICO_UPDATE: 'Bardo.tipos-servico.update',
  BARDO_TIPOS_SERVICO_DELETE: 'Bardo.tipos-servico.delete',

  // ========== BARDO - SERVIÇOS (ServicoController) ==========
  BARDO_SERVICOS_LIST: 'Bardo.servicos.list',
  BARDO_SERVICOS_READ: 'Bardo.servicos.read',
  BARDO_SERVICOS_CREATE: 'Bardo.servicos.create',
  BARDO_SERVICOS_UPDATE: 'Bardo.servicos.update',
  BARDO_SERVICOS_DELETE: 'Bardo.servicos.delete',

  // ========== BARDO - INTEGRAÇÃO WHATSAPP (WhatsappController) ==========
  BARDO_WHATSAPP_INTEGRACAO_LIST: 'Bardo.whatsapp-integracao.list',
  BARDO_WHATSAPP_INTEGRACAO_READ: 'Bardo.whatsapp-integracao.read',
  BARDO_WHATSAPP_INTEGRACAO_CREATE: 'Bardo.whatsapp-integracao.create',
  BARDO_WHATSAPP_INTEGRACAO_UPDATE: 'Bardo.whatsapp-integracao.update',
  BARDO_WHATSAPP_INTEGRACAO_DELETE: 'Bardo.whatsapp-integracao.delete',

  // ========== BARDO - FLUXOS DE ATENDIMENTO (FluxoAtendimentoController) ==========
  BARDO_FLUXOS_ATENDIMENTO_LIST: 'Bardo.fluxos-atendimento.list',
  BARDO_FLUXOS_ATENDIMENTO_READ: 'Bardo.fluxos-atendimento.read',
  BARDO_FLUXOS_ATENDIMENTO_CREATE: 'Bardo.fluxos-atendimento.create',
  BARDO_FLUXOS_ATENDIMENTO_UPDATE: 'Bardo.fluxos-atendimento.update',

  // ========== NOMOH - FLUXOS (FluxoController) ==========
  /**
   * Listar fluxos com filtros e paginação
   * Controller: FluxoController
   * Método: findAll
   */
  NOMOH_FLUXOS_LIST: 'Nomoh.fluxos.list',

  /**
   * Criar novo fluxo
   * Controller: FluxoController
   * Método: create
   */
  NOMOH_FLUXOS_CREATE: 'Nomoh.fluxos.create',

  /**
   * Visualizar detalhes de um fluxo específico
   * Controller: FluxoController
   * Método: findById
   */
  NOMOH_FLUXOS_READ: 'Nomoh.fluxos.read',

  /**
   * Atualizar fluxo existente
   * Controller: FluxoController
   * Método: update
   */
  NOMOH_FLUXOS_UPDATE: 'Nomoh.fluxos.update',

  /**
   * Excluir fluxo do sistema
   * Controller: FluxoController
   * Método: delete
   */
  NOMOH_FLUXOS_DELETE: 'Nomoh.fluxos.delete',

  // ========== NOMOH - FAQ (FaqController) ==========
  NOMOH_FAQ_LIST: 'Nomoh.faq.list',
  NOMOH_FAQ_CREATE: 'Nomoh.faq.create',
  NOMOH_FAQ_UPDATE: 'Nomoh.faq.update',
  NOMOH_FAQ_DELETE: 'Nomoh.faq.delete',

  // ========== NOMOH - CHAMADOS (ChamadoController) ==========
  NOMOH_CHAMADOS_LIST: 'Nomoh.chamados.list',
  NOMOH_CHAMADOS_RESPOND: 'Nomoh.chamados.respond',
  NOMOH_CHAMADOS_CLOSE: 'Nomoh.chamados.close',
} as const;

/**
 * Type-safe: previne erros de digitação
 * Uso: const key: PermissionKey = PERMISSIONS.NOMOH_PESSOAS_READ;
 */
export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * ✅ Helper para construir permissionKey dinamicamente
 * (útil para casos especiais ou testes)
 *
 * @param sistemaId - ID do sistema (use SistemaId enum)
 * @param module - Nome do módulo (ex: 'pessoas', 'permissions')
 * @param action - Ação (ex: 'read', 'create', 'update', 'delete')
 * @returns Permission key no formato: Sistema.module.action
 *
 * @example
 * buildPermissionKey(SistemaEnum.Nomoh, 'pessoas', 'read') // → 'Nomoh.pessoas.read'
 */
export const buildPermissionKey = (
  sistemaId: SistemaId,
  module: string,
  action: string
): string => {
  const sistemaNome = getSistemaNome(sistemaId);
  return `${sistemaNome}.${module}.${action}`;
};

/**
 * Helper interno para obter nome do sistema a partir do ID
 */
const getSistemaNome = (id: SistemaId): string => {
  switch (id) {
    case SistemaEnum.Nomoh:
      return 'Nomoh';
    case SistemaEnum.Bardo:
      return 'Bardo';
    case SistemaEnum.ApiZap:
      return 'ApiZap';
    default:
      return 'Unknown';
  }
};

/**
 * ✅ Helper para validar se uma string é uma permissão válida
 *
 * @param key - String a ser validada
 * @returns true se a string corresponde a uma permissão registrada
 *
 * @example
 * isValidPermission('Nomoh.pessoas.read') // → true
 * isValidPermission('Invalid.key') // → false
 */
export const isValidPermission = (key: string): key is PermissionKey => {
  return Object.values(PERMISSIONS).includes(key as PermissionKey);
};

/**
 * ✅ Helper para extrair partes de uma permission key
 *
 * @param key - Permission key completa
 * @returns Objeto com sistema, module e action
 *
 * @example
 * parsePermissionKey('Nomoh.pessoas.read')
 * // → { sistema: 'Nomoh', module: 'pessoas', action: 'read' }
 */
export const parsePermissionKey = (
  key: string
): { sistema: string; module: string; action: string } | null => {
  const parts = key.split('.');
  if (parts.length !== 3) return null;

  return {
    sistema: parts[0],
    module: parts[1],
    action: parts[2],
  };
};
